/**
 * MitarbeiterMinusAdmin.jsx — AP2 + AP4
 * Route: /admin/reporting/mitarbeiter-minus
 * Sidebar: Aufgaben → Reporting → Mitarbeiter-Minus
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Loader2, RefreshCw,
  AlertTriangle, CheckCircle2, Send, XCircle, MapPin,
  Plus, Trash2, PenLine, X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtEur = (v) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0) + '\u00a0€';
const fmtMonat = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

export default function MitarbeiterMinusAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState('');
  const [monat, setMonat] = useState(() => searchParams.get('monat') || new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [sending, setSending] = useState({});
  const [abzugDlg, setAbzugDlg] = useState(false);
  const [abzugForm, setAbzugForm] = useState({ mitarbeiter_id: '', betrag: '', grund: '', datum: new Date().toISOString().slice(0, 10) });
  const [empList, setEmpList] = useState([]);
  const [savingAbzug, setSavingAbzug] = useState(false);
  const highlightDone = useRef(false);

  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => {
        setLocations(r.data || []);
        const locParam = searchParams.get('location');
        const found = r.data?.find(l => l.id === locParam);
        if (found) setSelectedLoc(found.id);
        else if (r.data?.[0]) setSelectedLoc(r.data[0].id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { if (selectedLoc) fetchData(); }, [selectedLoc, monat]);

  // Highlight aus Push-Benachrichtigung: Karte automatisch aufklappen + hinscrollen
  useEffect(() => {
    if (!highlightId || !data?.mitarbeiter || highlightDone.current) return;
    const ma = data.mitarbeiter.find(m => (m.manuelle_abzuege || []).some(a => a.id === highlightId));
    if (ma) {
      highlightDone.current = true;
      setExpanded(e => ({ ...e, [ma.mitarbeiter_id]: true }));
      setTimeout(() => {
        document.getElementById(`abzug-${highlightId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlightId, data]);

  const openAbzugDlg = async () => {
    setAbzugForm({ mitarbeiter_id: '', betrag: '', grund: '', datum: new Date().toISOString().slice(0, 10) });
    setAbzugDlg(true);
    if (empList.length === 0) {
      try {
        const r = await axios.get(`${API}/api/kassensturz/mitarbeiter-liste`, { headers: authH() });
        setEmpList(r.data || []);
      } catch { toast.error('Mitarbeiterliste konnte nicht geladen werden'); }
    }
  };

  const handleAbzugSave = async () => {
    const emp = empList.find(e => e.id === abzugForm.mitarbeiter_id);
    if (!emp) return toast.error('Bitte Mitarbeiter wählen');
    const betrag = Number(String(abzugForm.betrag).replace(',', '.'));
    if (!betrag || betrag <= 0) return toast.error('Bitte gültigen Betrag eingeben');
    setSavingAbzug(true);
    try {
      await axios.post(`${API}/api/kassensturz/manuelle-abzuege`, {
        location_id: selectedLoc,
        mitarbeiter_id: emp.id, mitarbeiter_name: emp.name,
        betrag, grund: abzugForm.grund || null, datum: abzugForm.datum,
      }, { headers: authH() });
      toast.success(`Abzug für ${emp.name} eingetragen`);
      setAbzugDlg(false);
      const abzugMonat = abzugForm.datum.slice(0, 7);
      if (abzugMonat !== monat) setMonat(abzugMonat); else fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSavingAbzug(false); }
  };

  const confirmAbzugDelete = (msg, freigegeben) => {
    const warn = freigegeben
      ? '\n\n⚠️ ACHTUNG: Diese Abrechnung ist bereits FREIGEGEBEN – der Mitarbeiter kann sie unter "Meine Abrechnung" bereits sehen. Wenn du diesen Abzug entfernst, änderst du eine freigegebene Abrechnung.\nTipp: Ziehe ggf. zuerst die Abrechnung zurück.'
      : '';
    return window.confirm(msg + warn);
  };

  const handleAbzugDelete = async (a, ma) => {
    if (!confirmAbzugDelete(`Manuellen Abzug von ${fmtEur(a.betrag)} für ${a.mitarbeiter_name} wirklich entfernen?`, ma?.freigabe_erteilt)) return;
    try {
      await axios.delete(`${API}/api/kassensturz/manuelle-abzuege/${a.id}`, { headers: authH() });
      toast.success('Abzug entfernt');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  const handleSchichtAbzugDelete = async (s, ma) => {
    const d = new Date(s.datum).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit' });
    if (!confirmAbzugDelete(`Abzug von ${fmtEur(s.lohnabzug_betrag)} aus Schicht "${s.schicht_name}" (${d}) wirklich zurücknehmen?`, ma?.freigabe_erteilt)) return;
    try {
      await axios.delete(`${API}/api/kassensturz/schichten/${s.id}/lohnabzug`, { headers: authH() });
      toast.success('Abzug zurückgenommen');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler beim Zurücknehmen'); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/kassensturz/mitarbeiter-minus-summary`, {
        params: { location_id: selectedLoc, monat },
        headers: authH(),
      });
      setData(r.data);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const handleFreigabe = async (ma) => {
    setSending(s => ({ ...s, [ma.mitarbeiter_id]: true }));
    try {
      if (ma.freigabe_erteilt) {
        await axios.delete(`${API}/api/kassensturz/mitarbeiter-minus/freigabe`, {
          params: { location_id: selectedLoc, mitarbeiter_id: ma.mitarbeiter_id, monat },
          headers: authH(),
        });
        toast.success('Freigabe zurückgezogen');
      } else {
        await axios.post(`${API}/api/kassensturz/mitarbeiter-minus/freigabe`, {
          location_id: selectedLoc,
          mitarbeiter_id: ma.mitarbeiter_id,
          mitarbeiter_name: ma.mitarbeiter_name,
          monat,
        }, { headers: authH() });
        toast.success(`Abrechnung für ${ma.mitarbeiter_name} freigegeben`);
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSending(s => ({ ...s, [ma.mitarbeiter_id]: false })); }
  };

  const [freigebenAlle, setFreigebenAlle] = useState(false);

  // Teil-Freigabe: Auswahl je Mitarbeiter, vorbelegt aus Server-Status
  const [sel, setSel] = useState({});
  useEffect(() => {
    if (!data?.mitarbeiter) return;
    const init = {};
    data.mitarbeiter.forEach(m => {
      init[m.mitarbeiter_id] = {
        s: m.freigegebene_schicht_ids || [],
        a: m.freigegebene_abzug_ids || [],
      };
    });
    setSel(init);
  }, [data]);

  const toggleSel = (maId, type, id) => setSel(prev => {
    const cur = prev[maId] || { s: [], a: [] };
    const key = type === 's' ? 's' : 'a';
    const arr = cur[key].includes(id) ? cur[key].filter(x => x !== id) : [...cur[key], id];
    return { ...prev, [maId]: { ...cur, [key]: arr } };
  });

  // Ist dieser einzelne Tag/Eintrag bereits an den Mitarbeiter freigegeben?
  // voll = ganzer Monat freigegeben → alle Einträge freigegeben
  // teilweise = nur die IDs in freigegebene_*_ids
  const isReleased = (ma, kind, id) => {
    if (!ma.freigabe_erteilt) return false;
    if (ma.freigabe_modus !== 'teilweise') return true;
    const ids = kind === 's' ? ma.freigegebene_schicht_ids : ma.freigegebene_abzug_ids;
    return Array.isArray(ids) && ids.includes(id);
  };

  const handleAuswahlFreigabe = async (ma) => {
    const cur = sel[ma.mitarbeiter_id] || { s: [], a: [] };
    setSending(x => ({ ...x, [ma.mitarbeiter_id]: true }));
    try {
      await axios.post(`${API}/api/kassensturz/mitarbeiter-minus/freigabe-auswahl`, {
        location_id: selectedLoc, mitarbeiter_id: ma.mitarbeiter_id,
        mitarbeiter_name: ma.mitarbeiter_name, monat,
        schicht_ids: cur.s, abzug_ids: cur.a,
      }, { headers: authH() });
      const n = cur.s.length + cur.a.length;
      toast.success(n === 0 ? 'Freigabe entfernt' : `Teil-Freigabe: ${n} Eintrag/Einträge freigegeben`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSending(x => ({ ...x, [ma.mitarbeiter_id]: false })); }
  };

  const handleAlleFreigeben = async () => {
    const offen = (data?.mitarbeiter || []).filter(m => !m.freigabe_erteilt);
    if (offen.length === 0) return toast.info('Alle Abrechnungen dieses Monats sind bereits freigegeben');
    if (!window.confirm(`Abrechnung ${monat} für ${offen.length} Mitarbeiter freigeben? Jeder erhält eine Benachrichtigung. Bereits freigegebene werden übersprungen.`)) return;
    setFreigebenAlle(true);
    let ok = 0, fail = 0;
    for (const m of offen) {
      try {
        await axios.post(`${API}/api/kassensturz/mitarbeiter-minus/freigabe`, {
          location_id: selectedLoc, mitarbeiter_id: m.mitarbeiter_id,
          mitarbeiter_name: m.mitarbeiter_name, monat,
        }, { headers: authH() });
        ok++;
      } catch { fail++; }
    }
    setFreigebenAlle(false);
    if (fail === 0) toast.success(`${ok} Abrechnung${ok !== 1 ? 'en' : ''} freigegeben`);
    else toast.warning(`${ok} freigegeben, ${fail} fehlgeschlagen`);
    fetchData();
  };

  // Monatsauswahl: letzten 12 Monate (+ ggf. Monat aus URL-Param)
  const monatOptions = Array.from(new Set([
    ...Array.from({ length: 12 }, (_, i) => {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      return d.toISOString().slice(0, 7);
    }),
    monat,
  ])).sort().reverse();

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <h1 className="text-xl font-bold">Mitarbeiter-Minus</h1>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="secondary" onClick={handleAlleFreigeben} disabled={freigebenAlle || loading || !data}
              className="gap-1.5" data-testid="alle-freigeben-btn">
              {freigebenAlle ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Alle freigeben
            </Button>
            <Button size="sm" onClick={openAbzugDlg} className="gap-1.5" data-testid="add-abzug-btn">
              <Plus className="h-3.5 w-3.5" />
              Abzug hinzufügen
            </Button>
            <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-1.5">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          {locations.length > 1 && (
            <select value={selectedLoc} onChange={e => setSelectedLoc(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm bg-background">
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <select value={monat} onChange={e => setMonat(e.target.value)}
            className="rounded-xl border px-3 py-2 text-sm bg-background">
            {monatOptions.map(m => <option key={m} value={m}>{fmtMonat(m)}</option>)}
          </select>
        </div>

        {data && (
          <p className="text-xs text-muted-foreground">
            Bagatellgrenze: ±{fmtEur(data.bagatellgrenze)} — darunter kein Abzug
          </p>
        )}

        {loading && (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}

        {!loading && data?.mitarbeiter?.length === 0 && (
          <div className="text-center py-10 text-muted-foreground text-sm">
            Keine Schichten mit Mitarbeiter-Zuordnung in diesem Monat.
          </div>
        )}

        {data?.mitarbeiter?.map(ma => (
          <Card key={ma.mitarbeiter_id} className={`shadow-sm ${ma.ueber_bagatellgrenze ? 'border-red-300' : 'border-border'}`}
            data-testid={`ma-card-${ma.mitarbeiter_id}`}>
            <CardHeader className="pb-2 cursor-pointer select-none"
              onClick={() => setExpanded(e => ({ ...e, [ma.mitarbeiter_id]: !e[ma.mitarbeiter_id] }))}>
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {ma.ueber_bagatellgrenze
                    ? <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
                    : <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <span className="block truncate leading-tight">{ma.mitarbeiter_name}</span>
                    {ma.freigabe_erteilt && ma.freigabe_modus === 'teilweise' && (
                      <Badge variant="outline" className="mt-1 text-[10px] font-normal whitespace-nowrap text-amber-700 border-amber-300" data-testid={`badge-teilweise-${ma.mitarbeiter_id}`}>
                        Teilw. freigegeben ({(ma.freigegebene_schicht_ids?.length || 0) + (ma.freigegebene_abzug_ids?.length || 0)}/{(ma.schichten?.length || 0) + (ma.manuelle_abzuege?.length || 0)})
                      </Badge>
                    )}
                    {ma.freigabe_erteilt && ma.freigabe_modus !== 'teilweise' && (
                      <Badge variant="outline" className="mt-1 text-[10px] font-normal whitespace-nowrap text-green-700 border-green-300">Freigegeben</Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-sm font-bold tabular-nums ${ma.ueber_bagatellgrenze ? 'text-red-600' : 'text-muted-foreground'}`}>
                    −{fmtEur(ma.gesamt_minus)}
                  </span>
                  {expanded[ma.mitarbeiter_id] ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
                </div>
              </CardTitle>
            </CardHeader>

            {expanded[ma.mitarbeiter_id] && (
              <CardContent className="space-y-3">
                {/* Schichten-Tage */}
                <div className="space-y-1.5">
                  {ma.schichten.map(s => {
                    const rel = isReleased(ma, 's', s.id);
                    return (
                    <div key={s.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm px-2 py-1.5 rounded-lg bg-muted/30">
                      {rel ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" data-testid={`released-schicht-${s.id}`} />
                      ) : (
                        <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary cursor-pointer"
                          checked={(sel[ma.mitarbeiter_id]?.s || []).includes(s.id)}
                          onChange={() => toggleSel(ma.mitarbeiter_id, 's', s.id)}
                          title="Für Teil-Freigabe auswählen"
                          data-testid={`sel-schicht-${s.id}`} />
                      )}
                      <span className="text-muted-foreground w-20 shrink-0 text-xs">
                        {new Date(s.datum).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit' })}
                      </span>
                      <span className="flex-1 truncate">{s.schicht_name}</span>
                      <span className={`font-mono text-xs tabular-nums ${(s.differenz || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {(s.differenz || 0) >= 0 ? '+' : ''}{fmtEur(s.differenz || 0)}
                      </span>
                      {s.lohnabzug_betrag && (
                        <span className="text-xs text-amber-700 font-medium">
                          Abzug: −{fmtEur(s.lohnabzug_betrag)}
                        </span>
                      )}
                      {rel && (
                        <span className="flex items-center gap-1 text-[10px] font-medium text-green-700 shrink-0" data-testid={`freigegeben-label-schicht-${s.id}`}>
                          <CheckCircle2 className="h-3 w-3" />Freigegeben
                        </span>
                      )}
                      {s.lohnabzug_betrag && isAdmin && (
                        <button type="button" onClick={() => handleSchichtAbzugDelete(s, ma)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive shrink-0"
                          title="Abzug zurücknehmen" data-testid={`delete-schicht-abzug-${s.id}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>

                {/* Manuelle Abzüge */}
                {ma.manuelle_abzuege?.length > 0 && (
                  <div className="space-y-1.5">
                    {ma.manuelle_abzuege.map(a => {
                      const rel = isReleased(ma, 'a', a.id);
                      return (
                      <div key={a.id} id={`abzug-${a.id}`}
                        data-testid={`manueller-abzug-${a.id}`}
                        className={`px-2 py-1.5 rounded-lg text-sm space-y-0.5 ${
                          highlightId === a.id
                            ? 'ring-2 ring-amber-400 bg-amber-100/70 dark:bg-amber-900/30'
                            : 'bg-amber-50/60 dark:bg-amber-900/10'
                        }`}>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {rel ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" data-testid={`released-abzug-${a.id}`} />
                          ) : (
                            <input type="checkbox" className="h-4 w-4 shrink-0 accent-primary cursor-pointer"
                              checked={(sel[ma.mitarbeiter_id]?.a || []).includes(a.id)}
                              onChange={() => toggleSel(ma.mitarbeiter_id, 'a', a.id)}
                              title="Für Teil-Freigabe auswählen"
                              data-testid={`sel-abzug-${a.id}`} />
                          )}
                          <span className="text-muted-foreground w-20 shrink-0 text-xs">
                            {new Date(a.datum).toLocaleDateString('de-DE', { day:'2-digit', month:'2-digit' })}
                          </span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-amber-700 border-amber-300 shrink-0">
                            <PenLine className="h-2.5 w-2.5 mr-0.5" />Manuell
                          </Badge>
                          <span className="flex-1 truncate text-xs">{a.grund || 'Ohne Angabe'}</span>
                          <span className="font-mono text-xs tabular-nums text-red-600 font-semibold">
                            −{fmtEur(a.betrag)}
                          </span>
                          {rel && (
                            <span className="flex items-center gap-1 text-[10px] font-medium text-green-700 shrink-0" data-testid={`freigegeben-label-abzug-${a.id}`}>
                              <CheckCircle2 className="h-3 w-3" />Freigegeben
                            </span>
                          )}
                          {isAdmin && (
                            <button type="button" onClick={() => handleAbzugDelete(a, ma)}
                              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-destructive"
                              title="Löschen" data-testid={`delete-abzug-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground ml-20 pl-2">
                          eingetragen von {a.erstellt_von_name}{a.erstellt_von_rolle === 'stationsleitung' ? ' (SL)' : ''}
                        </p>
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Summe */}
                <div className="flex items-center justify-between px-2 py-2 border-t">
                  <span className="text-sm text-muted-foreground">Gesamt Abzüge festgelegt</span>
                  <span className="font-bold tabular-nums">−{fmtEur(ma.gesamt_abzug)}</span>
                </div>

                {/* Teil-Freigabe: ausgewählte Einträge gezielt freigeben */}
                {((ma.schichten?.length || 0) + (ma.manuelle_abzuege?.length || 0)) > 0 && (() => {
                  const cur = sel[ma.mitarbeiter_id] || { s: [], a: [] };
                  const n = cur.s.length + cur.a.length;
                  return (
                    <Button size="sm" variant="secondary" disabled={sending[ma.mitarbeiter_id]}
                      onClick={() => handleAuswahlFreigabe(ma)}
                      className="w-full gap-1.5"
                      data-testid={`auswahl-freigeben-btn-${ma.mitarbeiter_id}`}>
                      {sending[ma.mitarbeiter_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      {n > 0 ? `Auswahl freigeben (${n})` : 'Nur ausgewählte Tage freigeben'}
                    </Button>
                  );
                })()}

                {/* Freigabe-Button — ganzer Monat, für ALLE Mitarbeiter (auch 0,00 €) */}
                <Button
                    size="sm"
                    variant={ma.freigabe_erteilt ? 'outline' : 'default'}
                    disabled={sending[ma.mitarbeiter_id]}
                    onClick={() => handleFreigabe(ma)}
                    className="w-full gap-1.5"
                    data-testid={`freigabe-btn-${ma.mitarbeiter_id}`}
                  >
                    {sending[ma.mitarbeiter_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
                      ma.freigabe_erteilt ? <XCircle className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />}
                    {ma.freigabe_erteilt
                      ? 'Abrechnung zurückziehen'
                      : `Ganzen Monat für ${ma.mitarbeiter_name} freigeben`}
                  </Button>
                {ma.freigabe_am && (
                  <p className="text-[10px] text-muted-foreground text-center">
                    Freigegeben am {new Date(ma.freigabe_am).toLocaleDateString('de-DE')}
                  </p>
                )}
              </CardContent>
            )}
          </Card>
        ))}

        {/* ── Dialog: Manueller Abzug ─────────────────────────────────── */}
        {abzugDlg && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !savingAbzug && setAbzugDlg(false)}>
            <div className="bg-card rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-xl"
              onClick={e => e.stopPropagation()} data-testid="abzug-dialog">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <PenLine className="h-4 w-4 text-primary" />
                  Manuellen Abzug eintragen
                </h3>
                <button onClick={() => setAbzugDlg(false)}
                  className="p-1 rounded-full hover:bg-muted text-muted-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
                Für Abzüge ohne Kassendifferenz (z. B. nicht bezahlte Produkte). Zählt in die Monats-Abrechnung des Mitarbeiters.
              </p>

              <div className="space-y-1.5">
                <label className="text-xs font-medium">Mitarbeiter *</label>
                <select value={abzugForm.mitarbeiter_id}
                  onChange={e => setAbzugForm(f => ({ ...f, mitarbeiter_id: e.target.value }))}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm bg-background"
                  data-testid="abzug-mitarbeiter-select">
                  <option value="">— wählen —</option>
                  {empList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">Betrag (€) *</label>
                  <input inputMode="decimal" type="text" placeholder="0,00"
                    style={{ fontSize: '16px' }}
                    value={abzugForm.betrag}
                    onChange={e => setAbzugForm(f => ({ ...f, betrag: e.target.value.replace(/[^0-9.,]/g, '') }))}
                    className="w-full rounded-xl border px-3 py-2 text-right font-mono bg-background"
                    data-testid="abzug-betrag-input" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium">Datum</label>
                  <input type="date" value={abzugForm.datum}
                    onChange={e => setAbzugForm(f => ({ ...f, datum: e.target.value }))}
                    style={{ fontSize: '16px' }}
                    className="w-full rounded-xl border px-3 py-2 bg-background"
                    data-testid="abzug-datum-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium">Grund (optional)</label>
                <textarea rows={2} placeholder="z. B. Produkte nicht bezahlt"
                  style={{ fontSize: '16px' }}
                  value={abzugForm.grund}
                  onChange={e => setAbzugForm(f => ({ ...f, grund: e.target.value }))}
                  className="w-full rounded-xl border px-3 py-2 text-sm bg-background resize-none"
                  data-testid="abzug-grund-input" />
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setAbzugDlg(false)} disabled={savingAbzug}>
                  Abbrechen
                </Button>
                <Button className="flex-1 gap-1.5" onClick={handleAbzugSave} disabled={savingAbzug}
                  data-testid="abzug-save-btn">
                  {savingAbzug ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Eintragen
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
